// Typed fetch wrapper for the HTTP API. Attaches the Cognito ID token and
// surfaces the server error envelope as ApiError. A 401 clears the session
// so the route guard can send the user back to #/login.

import { getIdToken, signOut } from "./auth.js";

const BASE = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/+$/, "");

export function apiBaseUrl(): string {
  return BASE ?? "";
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
    readonly requestId?: string,
    readonly fieldErrors?: Record<string, string>,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

async function clearSession(): Promise<void> {
  try {
    await signOut();
  } catch {
    /* already signed out */
  }
  onUnauthorized?.();
}

export async function api<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  if (!BASE) throw new ApiError("VITE_API_URL is not configured.", 0, "CONFIG");

  const headers = new Headers(init.headers);
  if (!headers.has("Accept")) headers.set("Accept", "application/json");

  const token = await getIdToken();
  if (!token) {
    await clearSession();
    throw new ApiError("Not signed in.", 401, "UNAUTHORIZED");
  }
  headers.set("Authorization", `Bearer ${token}`);

  let body = init.body;
  if (init.json !== undefined) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(init.json);
  }

  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, { ...init, headers, body });
  } catch {
    throw new ApiError("You're offline. Try again when you're back online.", 0, "OFFLINE");
  }

  if (res.status === 401) {
    await clearSession();
    throw new ApiError("Session expired. Please sign in again.", 401, "UNAUTHORIZED");
  }

  const text = await res.text();
  const parsed = text ? (JSON.parse(text) as unknown) : null;

  if (!res.ok) {
    const envelope = parsed as {
      error?: { code?: string; message?: string; requestId?: string };
      errors?: Record<string, string>;
    } | null;
    const err = envelope?.error;
    throw new ApiError(
      err?.message ?? res.statusText,
      res.status,
      err?.code,
      err?.requestId,
      envelope?.errors,
    );
  }

  return parsed as T;
}

export function isOfflineError(err: unknown): boolean {
  return err instanceof ApiError && err.code === "OFFLINE";
}

export function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

const STREAM_URL = (import.meta.env.VITE_CHAT_STREAM_URL as string | undefined)?.replace(/\/+$/, "");

export function streamingEnabled(): boolean {
  return Boolean(STREAM_URL);
}

export type ChatResponse =
  | { type: "message"; sessionId: string; text: string }
  | {
      type: "proposal";
      sessionId: string;
      pendingId: string;
      confirmationText: string;
      events: import("@lifelog/shared").ProposedEvent[];
      text: string;
    };

export async function postChat(
  message: string,
  sessionId?: string,
  signal?: AbortSignal,
): Promise<ChatResponse> {
  return api<ChatResponse>("/chat", { method: "POST", json: { message, sessionId }, signal });
}

export async function confirmChat(
  pendingId: string,
  opts?: { keepIndexes?: number[]; timeMode?: "now" | "custom" | "proposed"; customIso?: string },
): Promise<{ saved: import("@lifelog/shared").SavedLogRef[] }> {
  return api("/chat/confirm", {
    method: "POST",
    json: {
      pendingId,
      keepIndexes: opts?.keepIndexes,
      timeMode: opts?.timeMode,
      customIso: opts?.customIso,
    },
  });
}

export interface StreamCallbacks {
  onSession?: (sessionId: string) => void;
  onDelta: (text: string) => void;
  onProposal: (p: {
    pendingId: string;
    confirmationText: string;
    events: import("@lifelog/shared").ProposedEvent[];
    sessionId: string;
  }) => void;
  onDone: (sessionId?: string) => void;
  onError: (message: string) => void;
}

export async function streamChat(
  message: string,
  sessionId: string | undefined,
  cb: StreamCallbacks,
  signal?: AbortSignal,
): Promise<void> {
  if (!STREAM_URL) throw new ApiError("Streaming is not configured.", 0, "CONFIG");
  const token = await getIdToken();
  if (!token) {
    await clearSession();
    throw new ApiError("Not signed in.", 401, "UNAUTHORIZED");
  }

  let res: Response;
  try {
    res = await fetch(STREAM_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ message, sessionId }),
      signal,
    });
  } catch (err) {
    if (isAbortError(err)) throw err;
    throw new ApiError("You're offline. Try again when you're back online.", 0, "OFFLINE");
  }

  if (res.status === 401) {
    await clearSession();
    throw new ApiError("Session expired. Please sign in again.", 401, "UNAUTHORIZED");
  }
  if (!res.ok || !res.body) {
    throw new ApiError(`Stream failed (${res.status})`, res.status);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  for (;;) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    let chunk: ReadableStreamReadResult<Uint8Array>;
    try {
      chunk = await reader.read();
    } catch (err) {
      if (signal?.aborted || isAbortError(err)) throw new DOMException("Aborted", "AbortError");
      throw err;
    }
    const { done, value } = chunk;
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let sep: number;
    while ((sep = buf.indexOf("\n\n")) >= 0) {
      const frame = buf.slice(0, sep);
      buf = buf.slice(sep + 2);
      const dataLine = frame.split("\n").find((l) => l.startsWith("data:"));
      if (!dataLine) continue;
      let evt: Record<string, unknown>;
      try {
        evt = JSON.parse(dataLine.slice(5).trim()) as Record<string, unknown>;
      } catch {
        continue;
      }
      switch (evt.type) {
        case "session":
          cb.onSession?.(String(evt.sessionId ?? ""));
          break;
        case "delta":
          cb.onDelta(String(evt.text ?? ""));
          break;
        case "proposal":
          cb.onProposal({
            pendingId: String(evt.pendingId ?? ""),
            confirmationText: String(evt.confirmationText ?? ""),
            events: (evt.events ?? []) as import("@lifelog/shared").ProposedEvent[],
            sessionId: String(evt.sessionId ?? ""),
          });
          break;
        case "done":
          cb.onDone(typeof evt.sessionId === "string" ? evt.sessionId : undefined);
          break;
        case "error":
          cb.onError(String(evt.message ?? "Stream error"));
          break;
      }
    }
  }
}
